import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { cn } from 'cn';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import type { ReactNode } from 'react';

const Combobox = ComboboxPrimitive.Root;

// Trigger-shaped input: same height/border/focus ring as SelectTrigger, but
// typing filters the list. Use for long lists (role types, countries, zones).
function ComboboxInput({
  className,
  size = 'default',
  strong = false,
  startIcon,
  hideTrigger = false,
  showClear = false,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  size?: 'sm' | 'default' | 'lg';
  strong?: boolean;
  startIcon?: ReactNode;
  hideTrigger?: boolean;
  /** Adds an × that empties the value; shown only while something is selected. */
  showClear?: boolean;
}) {
  return (
    <div
      data-slot="combobox-input"
      data-size={size}
      className={cn(
        'relative flex w-full items-center rounded-md border bg-transparent transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-2 has-[[aria-invalid=true]]:ring-destructive/30 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50 dark:bg-input/30 data-[size=default]:h-8 data-[size=lg]:h-10 data-[size=sm]:h-7',
        strong ? 'border-input-strong' : 'border-input',
        className,
      )}
    >
      {startIcon && (
        <span className="pointer-events-none pl-3 text-muted-foreground [&_svg]:size-4">
          {startIcon}
        </span>
      )}
      <ComboboxPrimitive.Input
        className={cn(
          'h-full min-w-0 flex-1 rounded-none border-0 bg-transparent py-2 pr-8 pl-2.5 text-sm outline-none placeholder:text-muted-foreground',
          size === 'lg' && 'pl-3 text-base',
          startIcon && 'pl-2',
          showClear && 'pr-14',
        )}
        {...props}
      />
      {showClear && (
        <ComboboxPrimitive.Clear
          aria-label="Clear"
          className="absolute inset-y-0 right-7 flex w-6 items-center justify-center text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground"
        >
          <XIcon className="size-3.5" />
        </ComboboxPrimitive.Clear>
      )}
      {!hideTrigger && (
        <ComboboxPrimitive.Trigger
          aria-label="Show options"
          className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-muted-foreground outline-none"
        >
          <ChevronDownIcon className="size-4" />
        </ComboboxPrimitive.Trigger>
      )}
    </div>
  );
}

function ComboboxContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'>) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            'relative isolate z-50 max-h-72 w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}
        >
          {children}
        </ComboboxPrimitive.Popup>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn('max-h-72 overflow-y-auto overscroll-contain p-1 data-empty:p-0', className)}
      {...props}
    />
  );
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn('py-3 text-center text-sm text-muted-foreground empty:hidden', className)}
      {...props}
    />
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={className} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn('px-1.5 py-1 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

export {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
};
